import { z } from 'zod';

/** Contratos de la conversión a MP4: lo que la interfaz enseña de un fichero y lo que
 *  puede pedir que se haga con cada pista.
 *
 *  Vive aquí y no en la API a propósito: la pantalla que edita las pistas y el planificador
 *  que construye el `ffmpeg` tienen que estar de acuerdo hasta en el nombre de cada campo, y
 *  con el esquema compartido un desajuste rompe la compilación en vez de una conversión. */

export const tipoPistaSchema = z.enum(['video', 'audio', 'subtitle']);
export type TipoPista = z.infer<typeof tipoPistaSchema>;

export const accionPistaSchema = z.enum(['copiar', 'recodificar', 'descartar', 'ocr']);
export type AccionPista = z.infer<typeof accionPistaSchema>;

/** Una pista tal como la ve la interfaz. Es la salida de `ffprobe` traducida: ni un solo
 *  nombre de campo de ffmpeg llega al navegador. */
export const pistaVistaSchema = z.object({
  /** Índice dentro de su tipo: es el que usa ffmpeg (`0:a:2`) y el que identifica la pista
   *  en un plan manual. */
  indice: z.number().int().min(0),
  tipo: tipoPistaSchema,
  codec: z.string(),
  idioma: z.string().nullable(),
  titulo: z.string().nullable(),
  porDefecto: z.boolean(),
  forzado: z.boolean(),
  bitrate: z.number().int().nullable(),
  canales: z.number().int().nullable(),
  tamanoBytes: z.number().int().nullable(),
  /** Subtítulo de imagen (VobSub, PGS): MP4 no los admite, hay que pasarlos por OCR. */
  esBitmap: z.boolean(),
});
export type PistaVista = z.infer<typeof pistaVistaSchema>;

export const analisisVistaSchema = z.object({
  duracionSegundos: z.number().nullable(),
  tieneCapitulos: z.boolean(),
  video: pistaVistaSchema.nullable(),
  alturaVideo: z.number().int().nullable(),
  anchoVideo: z.number().int().nullable(),
  /** `null` es SDR. `dolby_vision` es solo el perfil 5, el que no se puede pasar a SDR. */
  hdr: z.enum(['hdr10', 'hlg', 'dolby_vision']).nullable(),
  bitrateVideo: z.number().int().nullable(),
  audio: z.array(pistaVistaSchema),
  subtitulos: z.array(pistaVistaSchema),
});
export type AnalisisVista = z.infer<typeof analisisVistaSchema>;

/** Qué se hace con una pista y por qué. El motivo se enseña tal cual: sin él, una lista de
 *  «descartar» parece una pérdida arbitraria. */
export const filaPlanSchema = z.object({
  tipo: tipoPistaSchema,
  indice: z.number().int().min(0),
  accion: accionPistaSchema,
  motivo: z.string(),
  /** Posición en la salida, empezando en 1. `null` para lo que se descarta. */
  orden: z.number().int().nullable(),
});
export type FilaPlan = z.infer<typeof filaPlanSchema>;

/** Los metadatos y el destino de UNA pista.
 *
 *  No lleva códec a propósito: se deduce de lo que ya trae el fichero, que es lo que hace
 *  que un remux tarde segundos (D-13). Lo que se elige aquí es qué pistas entran, en qué
 *  orden y con qué etiquetas. */
export const ajustePistaSchema = z.object({
  indice: z.number().int().min(0),
  descartar: z.boolean().default(false),
  /** ISO 639-2 en minúsculas (`spa`, `eng`). `null` = sin identificar. */
  idioma: z
    .string()
    .trim()
    .min(2)
    .max(12)
    .nullable()
    .default(null),
  titulo: z.string().trim().max(200).nullable().default(null),
  porDefecto: z.boolean().default(false),
  forzado: z.boolean().default(false),
});
export type AjustePista = z.infer<typeof ajustePistaSchema>;

/** El plan que edita una persona.
 *
 *  **El orden de cada array ES el orden de las pistas en el MP4.** No hay campo `orden`
 *  aparte: dos fuentes de verdad para lo mismo acaban discrepando. */
export const planManualSchema = z.object({
  audio: z.array(ajustePistaSchema),
  subtitulos: z.array(ajustePistaSchema),
  /** `null` deja decidir a la regla automática: copiar si ya es H.264 y no pasa de la altura
   *  máxima; recodificar si no, reescalando lo que pase (D-24). `true`/`false` la fuerzan, y
   *  `false` manda también sobre el reescalado: sale con los píxeles que trae. */
  recodificarVideo: z.boolean().nullable().default(null),
});
export type PlanManual = z.infer<typeof planManualSchema>;

/** Qué formatos de subtítulo de imagen se pueden leer AQUÍ, con lo que hay instalado.
 *
 *  Los dos van por tesseract y son dos herramientas distintas, así que se preguntan por
 *  separado: en el contenedor `pgs` es cierto y `vobsub` no —`vobsub2srt` no compila
 *  contra tesseract 5—, y un solo booleano para los dos marcaba un VobSub para OCR y
 *  tumbaba la conversión entera al llegar (D-18) en vez de descartarlo al planificar. */
export const herramientasOcrSchema = z.object({
  /** PGS (`hdmv_pgs_subtitle`): `ffmpeg` extrae el `.sup` y lo lee `pgsrip`. */
  pgs: z.boolean(),
  /** VobSub (`dvd_subtitle`): `mkvextract` saca el par .idx/.sub y lo lee `vobsub2srt`. */
  vobsub: z.boolean(),
  tesseract: z.boolean(),
  mkvextract: z.boolean(),
  /** Los idiomas que tesseract tiene instalados, en ISO 639-2 y ordenados. Tener el binario
   *  no es tenerlo todo: cada idioma es un paquete aparte, y sin el suyo un subtítulo de
   *  imagen no se puede leer por mucho que estén pgsrip y tesseract. */
  idiomas: z.array(z.string()),
});
export type HerramientasOcr = z.infer<typeof herramientasOcrSchema>;

/** Lo que hace falta para pintar el editor de pistas de una descarga. */
export const pistasDescargaSchema = z.object({
  downloadId: z.string(),
  fileName: z.string(),
  rutaOriginal: z.string(),
  analisis: analisisVistaSchema,
  /** La propuesta automática, ya en forma editable: es el punto de partida y también lo
   *  que devuelve el botón «Aplicar propuesta». */
  propuesta: planManualSchema,
  /** Por qué la propuesta decide lo que decide, pista a pista. */
  motivos: z.array(filaPlanSchema),
  /** El plan que se guardó la última vez, si alguien ya lo editó. */
  guardado: planManualSchema.nullable(),
  ocr: herramientasOcrSchema,
});
export type PistasDescarga = z.infer<typeof pistasDescargaSchema>;

/** La cola de conversión: lo que está pasando por ffmpeg y lo que espera turno.
 *
 *  Vive en memoria de la API —una cola es una foto del momento, no un histórico— y por eso
 *  llega por su propio evento y no como parte de la descarga. La distinción que sostiene la
 *  pantalla es «esperando turno» contra «convirtiéndose»: en la base de datos las dos son
 *  `transcoding`, y sin este contrato una película con cinco delante se pintaría como si
 *  estuviera trabajando. */
export const estadoEnColaSchema = z.enum(['en_espera', 'en_curso']);
export type EstadoEnCola = z.infer<typeof estadoEnColaSchema>;

export const itemColaSchema = z.object({
  downloadId: z.string(),
  ed2kHash: z.string().nullable(),
  title: z.string(),
  year: z.number().int().nullable(),
  posterUrl: z.string().nullable(),
  estado: estadoEnColaSchema,
  /** 1 es la siguiente en entrar. `null` en lo que ya se está convirtiendo. */
  posicion: z.number().int().nullable(),
  encoladaEn: z.string(),
  iniciadaEn: z.string().nullable(),
});
export type ItemCola = z.infer<typeof itemColaSchema>;

export const colaConversionSchema = z.object({
  /** Cuántas conversiones caben a la vez ahora mismo (`transcodeConcurrency`). */
  limite: z.number().int(),
  items: z.array(itemColaSchema),
});
export type ColaConversionVista = z.infer<typeof colaConversionSchema>;

// ── Por dónde va una conversión ───────────────────────────────────────────────

/** En qué anda ahora mismo una conversión que está en curso.
 *
 *  Existe porque `transcoding` no es una sola cosa: antes de ffmpeg hay que analizar el
 *  original y, si el plan conserva subtítulos de imagen, leerlos por OCR. Ese OCR puede
 *  tardar MÁS que la propia conversión, y todo ese rato se pintaba como «Convirtiendo a
 *  MP4…» con la barra indefinida: ni se sabía que estaba leyendo subtítulos, ni si
 *  avanzaba, ni en cuál de ellos se había atascado. */
export const faseConversionSchema = z.enum(['analizando', 'ocr', 'convirtiendo']);
export type FaseConversion = z.infer<typeof faseConversionSchema>;

/** Qué se está haciendo con UN subtítulo de imagen. Extraerlo del original y leerlo son
 *  dos cosas de coste muy distinto —la primera son segundos, la segunda es la cara— y
 *  distinguirlas es lo que dice si se ha atascado y dónde. */
export const etapaOcrSchema = z.enum(['extrayendo', 'leyendo']);
export type EtapaOcr = z.infer<typeof etapaOcrSchema>;

export const avanceOcrSchema = z.object({
  /** Cuál se está leyendo, empezando en 1. */
  numero: z.number().int().min(1),
  total: z.number().int().min(1),
  /** Del subtítulo en curso, para poder decir cuál es sin abrir el registro. */
  idioma: z.string().nullable(),
  codec: z.string(),
  etapa: etapaOcrSchema,
  /** Cuántas imágenes trae el subtítulo en curso y cuántas van leídas.
   *
   *  De aquí sale el porcentaje, y es una cuenta de trabajo hecho, no una estimación: el
   *  fichero de imágenes se parte en bloques —el `.sup` del PGS por sus display sets, el
   *  `.idx` del VobSub por sus líneas— y cada bloque que termina son SUS imágenes leídas
   *  (D-19). `imagenes` es `null` en lo que no se puede contar: mientras se extrae la
   *  pista, y con un lector que no admita bloques. Entonces vuelve a haber solo reloj. */
  imagenes: z.number().int().nullable(),
  leidas: z.number().int().min(0),
  /** Segundos que lleva ESTE subtítulo en ESTA etapa. Es lo que dice que sigue vivo
   *  cuando aún no ha terminado ningún bloque, y con lo leído da el tiempo que falta. */
  segundos: z.number().min(0),
});
export type AvanceOcr = z.infer<typeof avanceOcrSchema>;

export const progresoConversionSchema = z.object({
  fase: faseConversionSchema,
  /** 0-100, o `null` cuando no hay nada que medir de verdad.
   *
   *  Durante el OCR es el porcentaje de IMÁGENES leídas sobre las de todos los subtítulos
   *  del plan, no una fracción del tiempo: las imágenes de un subtítulo cuestan más o
   *  menos lo mismo entre sí, así que se parece bastante, pero lo que cuenta es trabajo
   *  hecho. Sigue siendo `null` mientras se extrae la pista y en lo que no se puede
   *  contar. */
  porcentaje: z.number().nullable(),
  /** Solo con `fase: 'ocr'`. */
  ocr: avanceOcrSchema.nullable(),
});
export type ProgresoConversion = z.infer<typeof progresoConversionSchema>;

// ── El registro de una conversión ─────────────────────────────────────────────

/** De dónde sale cada línea. No es la gravedad, es la procedencia: `ffmpeg` son sus
 *  propios mensajes tal cual, y mezclarlos con los nuestros sin distinguirlos hace que
 *  buscar el error real sea leerlo todo. */
export const nivelRegistroSchema = z.enum(['info', 'aviso', 'error', 'ffmpeg']);
export type NivelRegistro = z.infer<typeof nivelRegistroSchema>;

export const lineaRegistroSchema = z.object({
  /** Número dentro del intento, empezando en 1. Es lo que permite mezclar sin duplicar lo
   *  que llegó en vivo con lo que se pidió por HTTP: las dos fuentes numeran igual. */
  n: z.number().int().min(1),
  ts: z.string(),
  nivel: nivelRegistroSchema,
  texto: z.string(),
});
export type LineaRegistro = z.infer<typeof lineaRegistroSchema>;

/** `en_curso` es lo que se está escribiendo AHORA: sus líneas viven en memoria de la API y
 *  llegan por evento. El resto ya están en la base de datos y no cambian. */
export const estadoIntentoSchema = z.enum(['en_curso', 'ok', 'fallo', 'cancelado']);
export type EstadoIntento = z.infer<typeof estadoIntentoSchema>;

/** Quién pidió esta conversión. Un fallo automático nada más descargar y un fallo tras
 *  editar las pistas se miran de forma distinta. */
export const origenIntentoSchema = z.enum([
  'automatico',
  'reintento',
  'reconversion',
  'altura',
  /** La revisión de una biblioteca (D-28) mandó a convertir un fichero que ya estaba en Plex. */
  'revision',
]);
export type OrigenIntento = z.infer<typeof origenIntentoSchema>;

export const intentoConversionSchema = z.object({
  id: z.string(),
  downloadId: z.string(),
  /** 1 es el primer intento de esta descarga. */
  numero: z.number().int().min(1),
  estado: estadoIntentoSchema,
  origen: origenIntentoSchema,
  iniciadoEn: z.string(),
  terminadoEn: z.string().nullable(),
  /** El mensaje con el que terminó, si terminó mal. Es el mismo que la descarga guarda en
   *  `errorDetail`: aquí va acompañado de todo lo que pasó antes. */
  error: z.string().nullable(),
  lineas: z.array(lineaRegistroSchema),
  /** `true` si se descartaron líneas del medio por el tope. Se dice, en vez de dejar un
   *  registro con un agujero que parece un cuelgue. */
  truncado: z.boolean(),
});
export type IntentoConversion = z.infer<typeof intentoConversionSchema>;

/** El registro completo de una descarga: cada vez que se intentó prepararla, en orden del
 *  más reciente al más antiguo. */
export const registroConversionSchema = z.object({
  downloadId: z.string(),
  intentos: z.array(intentoConversionSchema),
});
export type RegistroConversion = z.infer<typeof registroConversionSchema>;
